/**
 * Typed report form — dispatches by report type for all Stage 2 report types.
 *
 * Reports are stored as rows in `public.incidents`:
 *  - `report_type` identifies the form
 *  - `report_data` (jsonb) holds type-specific fields
 *  - `status='draft'` until the user submits
 *  - `restricted=true` for sensitive types (weapons discharge, use of force)
 *  - `parent_incident_id` links supplementals to the original report
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { AttachmentUploader } from "@/components/AttachmentUploader";
import {
  REPORT_TYPE_MAP,
  canCreateReport,
  type ReportType,
} from "@/lib/report-types";
import { INCIDENT_SEVERITIES } from "@/lib/incident-types";
import { z } from "zod";
import {
  Field,
  Section,
  EquipmentDamageFields,
  MaintenanceIssueFields,
  WeaponsDischargeFields,
  UseOfForceFields,
  MedicalIncidentFields,
  SuspiciousActivityFields,
  VisitorRemovalFields,
  SupplementalFields,
  ShiftStartFields,
  ShiftObservationFields,
  ShiftCloseOutFields,
} from "@/components/reports/ReportTypeFields";
import { Label } from "@/components/ui/label";

type SearchParams = { draftId?: string; parentId?: string };

export const Route = createFileRoute("/_authenticated/reports/new/$type")({
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    draftId: typeof s.draftId === "string" ? s.draftId : undefined,
    parentId: typeof s.parentId === "string" ? s.parentId : undefined,
  }),
  component: TypedReportForm,
  head: ({ params }) => ({
    meta: [{ title: `${REPORT_TYPE_MAP[params.type as ReportType]?.short ?? "Report"} — MissionReady` }],
  }),
});

type Site = { id: string; name: string };

function TypedReportForm() {
  const { type } = Route.useParams();
  const { draftId, parentId: parentIdSearch } = Route.useSearch();
  const navigate = useNavigate();
  const { currentOrg, user, currentRole } = useAuth();
  const meta = REPORT_TYPE_MAP[type as ReportType];

  const [sites, setSites] = useState<Site[]>([]);
  const [busy, setBusy] = useState(false);
  const [reportId, setReportId] = useState<string | null>(draftId ?? null);

  // Common fields
  const [siteId, setSiteId] = useState("");
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<string>(meta?.defaultSeverity ?? "medium");
  const [occurrence, setOccurrence] = useState(new Date().toISOString().slice(0, 16));
  const [location, setLocation] = useState("");
  const [narrative, setNarrative] = useState("");
  const [parentIncidentId, setParentIncidentId] = useState<string | null>(parentIdSearch ?? null);

  // Type-specific payload
  const [data, setData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!currentOrg) return;
    void supabase
      .from("sites")
      .select("id,name")
      .eq("organization_id", currentOrg.id)
      .eq("active", true)
      .then(({ data: ss }) => setSites((ss as Site[]) ?? []));
  }, [currentOrg?.id]);

  // Load existing draft if editing.
  useEffect(() => {
    if (!draftId) return;
    void supabase
      .from("incidents")
      .select("*")
      .eq("id", draftId)
      .single()
      .then(({ data: row }) => {
        if (!row) return;
        setSiteId(row.site_id ?? "");
        setTitle(row.title ?? "");
        setSeverity(row.severity);
        setOccurrence(new Date(row.occurrence_at).toISOString().slice(0, 16));
        setLocation(row.location_description ?? "");
        setNarrative(row.narrative ?? "");
        setData((row.report_data as Record<string, any>) ?? {});
        setParentIncidentId(row.parent_incident_id ?? null);
      });
  }, [draftId]);

  if (!meta) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold">Unknown report type</h1>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/reports/new">Choose a report type</Link>
        </Button>
      </div>
    );
  }

  if (!canCreateReport(meta, currentRole)) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
        <h1 className="mt-2 font-display text-2xl font-bold">Restricted</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {meta.label} can only be filed by supervisors or admins.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/reports/new">Back</Link>
        </Button>
      </div>
    );
  }

  const upsert = async (status: "draft" | "open"): Promise<string | null> => {
    if (!currentOrg || !user) return null;

    if (status === "open") {
      const schema = z.object({
        narrative: z.string().trim().min(10, "Narrative must be at least 10 characters").max(10000),
        title: z.string().trim().max(200).optional(),
        location: z.string().trim().max(500).optional(),
      });
      const parsed = schema.safeParse({ narrative, title, location });
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? "Please complete required fields");
        return null;
      }
      if (meta.type === "supplemental" && !parentIncidentId) {
        toast.error("Link the original report this supplements before submitting.");
        return null;
      }
    }

    const payload = {
      organization_id: currentOrg.id,
      reporter_id: user.id,
      site_id: siteId || null,
      category: meta.defaultCategory,
      subtype: meta.short,
      severity,
      title: title || null,
      narrative: narrative || null,
      location_description: location || null,
      occurrence_at: new Date(occurrence).toISOString(),
      status,
      report_type: meta.type,
      report_data: data,
      restricted: meta.restrictedRecord ?? false,
      parent_incident_id: parentIncidentId,
    };

    if (reportId) {
      const { error } = await supabase.from("incidents").update(payload).eq("id", reportId);
      if (error) throw error;
      return reportId;
    }
    const { data: ins, error } = await supabase
      .from("incidents")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw error;
    setReportId(ins.id);
    return ins.id;
  };

  const onSaveDraft = async () => {
    setBusy(true);
    try {
      const id = await upsert("draft");
      if (id) toast.success("Draft saved");
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async () => {
    setBusy(true);
    try {
      const id = await upsert("open");
      if (id) {
        toast.success("Report submitted");
        navigate({ to: "/incidents/$incidentId", params: { incidentId: id } });
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const Icon = meta.icon;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-6 md:px-8 md:py-8 md:pb-8">
      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/reports">
          <ArrowLeft className="mr-1 h-4 w-4" /> All reports
        </Link>
      </Button>

      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-2xl font-bold md:text-3xl">{meta.label}</h1>
            {meta.restrictedRecord && (
              <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-destructive">
                <ShieldAlert className="h-3 w-3" /> Restricted
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{meta.description}</p>
        </div>
      </div>

      {/* Common section */}
      <section className="mt-6 grid gap-4 rounded-xl border border-border bg-card p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Site">
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger aria-label="Optional" className="h-11"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Severity">
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INCIDENT_SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Occurrence date & time">
          <Input type="datetime-local" className="h-11" value={occurrence} onChange={(e) => setOccurrence(e.target.value)} />
        </Field>
        <Field label="Short title (optional)">
          <Input className="h-11" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Brief summary" />
        </Field>
        <Field label="Location details">
          <Input className="h-11" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. North parking lot, level 2" />
        </Field>
        <Field label="Narrative *">
          <Textarea rows={6} value={narrative} onChange={(e) => setNarrative(e.target.value)} placeholder="What happened, in chronological detail." />
        </Field>
      </section>

      {/* Type-specific sections */}
      {meta.type === "critical_incident" && (
        <CriticalIncidentFields data={data} setData={setData} />
      )}
      {meta.type === "company_car_accident" && (
        <VehicleAccidentFields data={data} setData={setData} />
      )}
      {meta.type === "equipment_damage" && (
        <EquipmentDamageFields data={data} setData={setData} />
      )}
      {meta.type === "maintenance_issue" && (
        <MaintenanceIssueFields data={data} setData={setData} />
      )}
      {meta.type === "weapons_discharge" && (
        <WeaponsDischargeFields data={data} setData={setData} />
      )}
      {meta.type === "use_of_force" && (
        <UseOfForceFields data={data} setData={setData} />
      )}
      {meta.type === "medical_incident" && (
        <MedicalIncidentFields data={data} setData={setData} />
      )}
      {meta.type === "suspicious_activity" && (
        <SuspiciousActivityFields data={data} setData={setData} />
      )}
      {meta.type === "visitor_removal" && (
        <VisitorRemovalFields data={data} setData={setData} />
      )}
      {meta.type === "shift_start" && (
        <ShiftStartFields data={data} setData={setData} />
      )}
      {meta.type === "shift_observation" && (
        <ShiftObservationFields data={data} setData={setData} />
      )}
      {meta.type === "shift_close_out" && (
        <ShiftCloseOutFields data={data} setData={setData} />
      )}
      {meta.type === "supplemental" && currentOrg && (
        <SupplementalFields
          data={data}
          setData={setData}
          orgId={currentOrg.id}
          parentId={parentIncidentId}
          onParentChange={setParentIncidentId}
        />
      )}

      {/* Attachments — requires a saved row first */}
      <section className="mt-4">
        {reportId && currentOrg && user ? (
          <AttachmentUploader
            incidentId={reportId}
            orgId={currentOrg.id}
            userId={user.id}
            canDelete
          />
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-card p-5 text-sm text-muted-foreground">
            Save a draft to enable photo, video, and file attachments.
          </div>
        )}
      </section>

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+72px)] z-30 px-4 sm:bottom-4">
        <div className="mx-auto flex max-w-3xl gap-2 rounded-xl border border-border bg-card/95 p-2 shadow-xl backdrop-blur">
          <Button
            type="button"
            variant="outline"
            className="h-12 flex-1"
            onClick={onSaveDraft}
            disabled={busy}
          >
            <Save className="mr-1 h-4 w-4" /> Save draft
          </Button>
          <Button type="button" className="h-12 flex-1" onClick={onSubmit} disabled={busy}>
            <Send className="mr-1 h-4 w-4" /> {busy ? "Submitting…" : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---- Stage 1 forms preserved for backward compatibility ---- */

function CriticalIncidentFields({
  data, setData,
}: { data: Record<string, any>; setData: (d: Record<string, any>) => void }) {
  const set = (k: string, v: any) => setData({ ...data, [k]: v });
  return (
    <Section title="Critical incident details">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Report type">
          <Select value={data.subType ?? "original"} onValueChange={(v) => set("subType", v)}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="original">Original</SelectItem>
              <SelectItem value="continuation">Continuation</SelectItem>
              <SelectItem value="supplemental">Supplemental / Follow-Up</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Incident type">
          <Input className="h-11" value={data.incidentType ?? ""} onChange={(e) => set("incidentType", e.target.value)} placeholder="e.g. Assault, Theft" />
        </Field>
      </div>
      <Field label="Specific location on site">
        <Input className="h-11" value={data.specificLocation ?? ""} onChange={(e) => set("specificLocation", e.target.value)} />
      </Field>
      <Field label="Responding agencies">
        <Input className="h-11" value={data.respondingAgencies ?? ""} onChange={(e) => set("respondingAgencies", e.target.value)} placeholder="Police, EMS, Fire…" />
      </Field>
      <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-3">
        <div>
          <Label className="text-sm font-medium">Notify leadership</Label>
          <p className="text-xs text-muted-foreground">Flag for supervisor/admin alert on submit.</p>
        </div>
        <Switch checked={!!data.notifyLeadership} onCheckedChange={(v) => set("notifyLeadership", v)} />
      </div>
    </Section>
  );
}

function VehicleAccidentFields({
  data, setData,
}: { data: Record<string, any>; setData: (d: Record<string, any>) => void }) {
  const set = (k: string, v: any) => setData({ ...data, [k]: v });
  return (
    <>
      <Section title="Vehicle information">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vehicle ID / unit #">
            <Input aria-label="Notify leadership" className="h-11" value={data.vehicleId ?? ""} onChange={(e) => set("vehicleId", e.target.value)} />
          </Field>
          <Field label="License plate">
            <Input className="h-11" value={data.plate ?? ""} onChange={(e) => set("plate", e.target.value)} />
          </Field>
          <Field label="Make / model">
            <Input className="h-11" value={data.makeModel ?? ""} onChange={(e) => set("makeModel", e.target.value)} />
          </Field>
          <Field label="Driver">
            <Input className="h-11" value={data.driver ?? ""} onChange={(e) => set("driver", e.target.value)} />
          </Field>
        </div>
        <Field label="Damage description">
          <Textarea rows={3} value={data.damage ?? ""} onChange={(e) => set("damage", e.target.value)} />
        </Field>
      </Section>

      <Section title="Police & injuries">
        <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-3">
          <Label className="text-sm font-medium">Police report filed?</Label>
          <Switch checked={!!data.policeFiled} onCheckedChange={(v) => set("policeFiled", v)} />
        </div>
        {data.policeFiled && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Agency"><Input aria-label="Police report filed?" className="h-11" value={data.policeAgency ?? ""} onChange={(e) => set("policeAgency", e.target.value)} /></Field>
            <Field label="Officer name"><Input className="h-11" value={data.officerName ?? ""} onChange={(e) => set("officerName", e.target.value)} /></Field>
            <Field label="Badge #"><Input className="h-11" value={data.badge ?? ""} onChange={(e) => set("badge", e.target.value)} /></Field>
            <Field label="Report #"><Input className="h-11" value={data.policeReportNo ?? ""} onChange={(e) => set("policeReportNo", e.target.value)} /></Field>
          </div>
        )}

        <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-3">
          <Label className="text-sm font-medium">Injuries?</Label>
          <Switch checked={!!data.injuries} onCheckedChange={(v) => set("injuries", v)} />
        </div>
        {data.injuries && (
          <>
            <Field label="Injury description">
              <Textarea aria-label="Injuries?" rows={3} value={data.injuryDesc ?? ""} onChange={(e) => set("injuryDesc", e.target.value)} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-3">
                <Label className="text-sm font-medium">EMS responded</Label>
                <Switch checked={!!data.emsResponded} onCheckedChange={(v) => set("emsResponded", v)} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-3">
                <Label className="text-sm font-medium">Hospital transport</Label>
                <Switch checked={!!data.hospitalTransport} onCheckedChange={(v) => set("hospitalTransport", v)} />
              </div>
            </div>
          </>
        )}
      </Section>
    </>
  );
}
